import currency from "currency.js";
import moment from "moment";
import { useContext, useMemo, useState } from "react";
import { DataContext } from "../../context/DataContext";
import {
  BACKGROUND_COLORS,
  BORDER_COLORS,
  COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { FONT_STYLE } from "../../constants/design/fontSize";
import { SPACING } from "../../constants/design/spacing";
import {
  IKillSwitchUsageEntry,
  useKillSwitchUsage,
} from "../../hooks/api/admin/useKillSwitchUsage";
import { Spinner } from "../shared/Spinner";
import { shortenString } from "../../utils/strings";

const fmtUsd = (raw?: string): string => {
  if (!raw) return "—";
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return currency(n, { precision: 2 }).format();
};

const fmtTime = (iso?: string): string =>
  iso ? moment(iso).format("DD MMM HH:mm:ss [UTC]") : "—";

// KillSwitchUsage shows the per-withdrawal breakdown that sums to the current
// kill-switch counter, plus archived prior windows. Its reason for existing:
// answer "is this $190k counter one big withdrawal, or the same $10k
// resubmitted 19 times?" at a glance — the `submissions` column makes
// resubmission inflation obvious (and rows with submissions > 1 are flagged).
//
// Collapsed by default and only fetches while open (the breakdown is heavier
// than the 10s status poll in KillSwitchControls).
export function KillSwitchUsage() {
  const { records } = useContext(DataContext);
  const [expanded, setExpanded] = useState<boolean>(false);
  const { data, isLoading, error } = useKillSwitchUsage();

  const current = useMemo(() => data?.current ?? [], [data]);
  const history = useMemo(() => data?.history ?? [], [data]);
  const resubmittedCount = useMemo(
    () => current.filter((e) => e.submissions > 1).length,
    [current]
  );

  const accountLabel = (account: string) =>
    records[account] || shortenString(account, 6, 4);

  const renderEntryRows = (entries: IKillSwitchUsageEntry[]) =>
    entries.map((e) => {
      const inflated = e.submissions > 1;
      return (
        <tr
          key={e.id}
          style={{
            borderTop: `1px solid ${BORDER_COLORS.one}`,
            background: inflated ? COLORS.red.five : "transparent",
          }}
        >
          <td style={{ padding: SPACING.two }}>
            <a
              href={`/account/${e.account}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: records[e.account] ? COLORS.blue.one : TEXT_COLORS.one,
                textDecoration: "none",
              }}
            >
              {accountLabel(e.account)}
            </a>
          </td>
          <td
            style={{
              padding: SPACING.two,
              textAlign: "right",
              color: TEXT_COLORS.one,
            }}
          >
            {fmtUsd(e.value)}
          </td>
          <td
            style={{
              padding: SPACING.two,
              textAlign: "right",
              color: inflated ? COLORS.red.one : TEXT_COLORS.two,
              fontWeight: inflated ? 600 : 400,
            }}
            title={
              inflated
                ? "Resubmitted: same withdrawal counted once, but it re-entered the stream this many times"
                : undefined
            }
          >
            {e.submissions}
            {inflated ? " ⚠" : ""}
          </td>
          <td style={{ padding: SPACING.two, color: TEXT_COLORS.two }}>
            {shortenString(e.id, 6, 4)}
          </td>
          <td style={{ padding: SPACING.two, color: TEXT_COLORS.two }}>
            {fmtTime(e.first_seen)}
          </td>
          <td style={{ padding: SPACING.two, color: TEXT_COLORS.two }}>
            {fmtTime(e.last_seen)}
          </td>
        </tr>
      );
    });

  const tableHead = (
    <thead>
      <tr style={{ color: TEXT_COLORS.two, textAlign: "left" }}>
        <th style={{ padding: SPACING.two }}>Account</th>
        <th style={{ padding: SPACING.two, textAlign: "right" }}>Value</th>
        <th style={{ padding: SPACING.two, textAlign: "right" }}>Submissions</th>
        <th style={{ padding: SPACING.two }}>Withdrawal ID</th>
        <th style={{ padding: SPACING.two }}>First Seen</th>
        <th style={{ padding: SPACING.two }}>Last Seen</th>
      </tr>
    </thead>
  );

  return (
    <div
      style={{
        borderBottom: `1px solid ${BORDER_COLORS.one}`,
        background: BACKGROUND_COLORS.componentSolid,
        display: "flex",
        flexDirection: "column",
        minHeight: 40,
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: SPACING.two,
          padding: `0 ${SPACING.three}px`,
          height: 40,
          background: "transparent",
          border: "none",
          borderBottom: expanded ? `1px solid ${BORDER_COLORS.one}` : "none",
          color: TEXT_COLORS.one,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span
          style={{
            ...FONT_STYLE.label.two,
            color: TEXT_COLORS.two,
            whiteSpace: "nowrap",
          }}
        >
          Counter Breakdown
        </span>
        <span
          style={{
            ...FONT_STYLE.label.one,
            color: TEXT_COLORS.one,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {isLoading && !data
            ? "…"
            : `${current.length} withdrawal${
                current.length === 1 ? "" : "s"
              }`}
        </span>
        {resubmittedCount > 0 ? (
          <span
            style={{
              ...FONT_STYLE.label.two,
              color: COLORS.red.one,
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            ⚠ {resubmittedCount} resubmitted
          </span>
        ) : null}
        <span
          style={{
            ...FONT_STYLE.label.two,
            color: TEXT_COLORS.three,
            whiteSpace: "nowrap",
          }}
        >
          sums to {fmtUsd(data?.used)}
        </span>

        <div style={{ flex: 1 }} />

        {error ? (
          <span
            style={{
              ...FONT_STYLE.label.two,
              color: COLORS.red.one,
              whiteSpace: "nowrap",
            }}
          >
            Failed to load
          </span>
        ) : null}
        <span
          style={{
            ...FONT_STYLE.label.two,
            color: TEXT_COLORS.two,
            whiteSpace: "nowrap",
          }}
        >
          {expanded ? "▾ Hide" : "▸ Show"}
        </span>
      </button>

      {expanded ? (
        <div style={{ overflow: "auto", maxHeight: 360 }}>
          {isLoading && !data ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: SPACING.four,
              }}
            >
              <Spinner />
            </div>
          ) : null}

          {!isLoading && current.length === 0 ? (
            <div
              style={{
                padding: SPACING.three,
                color: TEXT_COLORS.two,
                ...FONT_STYLE.label.two,
              }}
            >
              No usage recorded in the current window. (The ledger is
              populated going forward — a counter set before this was deployed
              shows no breakdown until the next window.)
            </div>
          ) : null}

          {current.length > 0 ? (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                ...FONT_STYLE.label.two,
              }}
            >
              {tableHead}
              <tbody>{renderEntryRows(current)}</tbody>
            </table>
          ) : null}

          {history.length > 0 ? (
            <div style={{ padding: SPACING.three }}>
              <div
                style={{
                  ...FONT_STYLE.label.two,
                  color: TEXT_COLORS.two,
                  marginBottom: SPACING.two,
                }}
              >
                Past windows (last 2 days)
              </div>
              {history.map((win) => (
                <div
                  key={win.reset_at}
                  style={{
                    border: `1px solid ${BORDER_COLORS.one}`,
                    borderRadius: 4,
                    marginBottom: SPACING.two,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: SPACING.two,
                      alignItems: "center",
                      padding: SPACING.two,
                      color: TEXT_COLORS.two,
                      ...FONT_STYLE.label.two,
                    }}
                  >
                    <span style={{ color: TEXT_COLORS.one, fontWeight: 600 }}>
                      {fmtUsd(win.total)}
                    </span>
                    <span>reset {fmtTime(win.reset_at)}</span>
                    {win.tripped ? (
                      <span style={{ color: COLORS.red.one }}>tripped</span>
                    ) : null}
                  </div>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      ...FONT_STYLE.label.two,
                    }}
                  >
                    {tableHead}
                    <tbody>{renderEntryRows(win.entries)}</tbody>
                  </table>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
